import { createTheme } from "@mui/material";

// ─── SHARED CONFIG ─────────────────────────────────────────────
const sharedTypography = {
  fontFamily: ["Nunito", "Arial", "sans-serif"].join(","),
};

const sharedZIndex = {
  modal: 1305,
};

// Scrollbar styles
const getScrollbar = (theme) => ({
  html: {
    "*::-webkit-scrollbar": {
      width: "3px",
      height: "6px",
    },
    "*::-webkit-scrollbar-track": {
      background: "inherit",
      boxShadow: "inset 0 0 6px rgba(0,0,0,0.3)",
    },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.divider,
      borderRadius: "20px",
    },
    "*::-webkit-scrollbar-thumb:horizontal": {
      backgroundColor: theme.palette.divider,
    },
  },
});

// Shared component overrides (🔥 important)
const getSharedComponents = (theme) => ({
  MuiCssBaseline: {
    styleOverrides: {
      ...getScrollbar(theme),
      body: {
        transition: "background-color 0.3s ease, color 0.3s ease",
      },
    },
  },

  MuiButton: {
    defaultProps: {
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: {
        "&:focus-visible": {
          boxShadow: `0px 1px 10px ${theme.palette.primary.main}`,
        },
      },
    },
  },

  MuiTab: {
    defaultProps: {
      disableFocusRipple: true,
    },
    styleOverrides: {
      root: {
        "&:focus-visible": {
          boxShadow: `0px 10px 20px ${theme.palette.primary.main}`,
        },
      },
    },
  },
});

// ─── BASE THEME CREATOR ────────────────────────────────────────
const createAppTheme = (paletteConfig, extraComponents = {}) => {
  const theme = createTheme({
    typography: sharedTypography,
    zIndex: sharedZIndex,
    palette: paletteConfig,
  });

  theme.components = {
    ...getSharedComponents(theme),
    ...extraComponents,
  };

  return theme;
};

// ─── THEMES ───────────────────────────────────────────────────

//Default Theme
export const defaultTheme = createAppTheme(
  {
    mode: "light",
    primary: { main: "#F77200" }, // your original orange
    background: { default: "#FFFFFF", paper: "#FFFFFF" },
    text: { primary: "#3b3434de", secondary: "#374151" },
    divider: "#E0E0E0",
  },
  {
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: "#3b3434de",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFFFF",
          color: "#3b3434de",
        },
      },
    },
  }
);
export const warmTheme = createAppTheme(
  {
    mode: "light",
    primary: { main: "#F77200" },
    secondary: { main: "#C2410C" },
    background: { default: "#FFF8E1", paper: "#FFFDF5" },
    text: { primary: "#3b3434de", secondary: "#6B4F2C" },
    divider: "#F3D7C0",
  },
  {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFDF5",
          color: "#3b3434de",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFF8E1",
          color: "#3b3434de",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#FFFDF5",
          color: "#3b3434de",
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          backgroundColor: "inherit",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFDF5",
        },
      },
    },
  }
);

export const coolTheme = createAppTheme(
  {
    mode: "light",
    primary: { main: "#0288d1" },
    secondary: { main: "#01579b" },
    background: { default: "#EDF6FB", paper: "#F7FBFF" },
    text: { primary: "#1f2d3d", secondary: "#4b6b88" },
    divider: "#D6E4F0",
  },
  {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#F7FBFF",
          color: "#1f2d3d",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#EDF6FB",
          color: "#1f2d3d",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#F7FBFF",
          color: "#1f2d3d",
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          backgroundColor: "inherit",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#F7FBFF",
        },
      },
    },
  }
);
// ⚫ Dark
export const darkTheme = createAppTheme(
  {
    mode: "dark",
    primary: { main: "#F77200" },
    secondary: { main: "#2052bd" },
    background: { default: "#121212", paper: "#1e1e1e" },
    text: { primary: "#e0e0e0", secondary: "#cccccc" },
    divider: "#333333",
  },
  {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e",
          color: "var(--text-color)",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e",
          color: "#e0e0e0",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1e1e1e",
          color: "#e0e0e0",
        },
      },
    },
    MuiBox: {
      styleOverrides: {
        root: {
          backgroundColor: "inherit",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e1e1e",
        },
      },
    },
  }
);

// ─── THEME SWITCHER ───────────────────────────────────────────
const themeMap = {
  default: defaultTheme,
  warm: warmTheme,
  cool: coolTheme,
  dark: darkTheme,
};

export const getThemeByMode = (mode) => themeMap[mode] || defaultTheme;